import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Role } from '../types/auth.types';
import { tryRefreshUserPermissions, getEmbedUrl } from '../services/powerBiAuth';
import { loginRequest } from '../services/msalConfig';
import { getUsersRole, adminUsers, croUsers, customerSuccessUsers, viewerUsers } from '../../../Config';
import { updateUserInfo } from '../../../utils/setupFetchLogging';
import { InteractionStatus, EventMessage, EventType, AuthError, AuthenticationResult } from '@azure/msal-browser';
import { ApprovalStatus, Preset } from '../../../pages/main/components/Preset/models';

interface AuthState {
  accessToken: string;
  embedUrl: string;
  username: string;
  displayName: string;
  userId: string;
  userRole: Role;
  isAuthenticated: boolean;
  error: string[];
  isLoading: boolean;
  isInitialized: boolean;
  _msalInstance?: any;
}

interface AuthActions {
  logout: () => Promise<void>;
  _setMsalInstance: (instance: any) => void;
  initializeAuthentication: (msalContext: {
    instance: any;
    accounts: any[];
    inProgress: InteractionStatus;
  }) => Promise<void>;
  handleLoginSuccess: (payload: AuthenticationResult) => Promise<void>;
  handleAuthError: (error: AuthError, msalInstance: any, loginRequest: any) => void;
  handleSilentTokenAcquisition: (account: any, msalInstance: any, requestWithAccount: any) => Promise<void>;
  acquireEmbedUrl: () => Promise<void>;
  hasRole: (roles: Role[]) => boolean;
  isAdminUser: () => boolean;
  isViewerUser: () => boolean;
  isCROUser: () => boolean;
  isCustomerSuccessUser: () => boolean;
  isBasicUser: () => boolean;
  canDiscount: () => boolean;
  getRole: (email?: string) => Role;
  canApproveRejectApprover: (preset: Preset) => boolean;
  canApproveRejectCRO: (preset: Preset) => boolean;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  accessToken: '',
  embedUrl: '',
  username: '',
  displayName: '',
  userId: '',
  userRole: Role.BASIC,
  isAuthenticated: false,
  error: [],
  isLoading: false,
  isInitialized: false,
  _msalInstance: undefined,
};

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      logout: async () => {
        const { _msalInstance } = get();
        if (_msalInstance) {
          try {
            await _msalInstance.logoutRedirect();
          } catch (error) {
            console.error('Logout failed:', error);
            set({ error: ['Logout failed. Please try again.'] }, false, 'logout-error');
          }
        } else {
          console.warn('MSAL instance not available for logout');
          set(
            {
              ...initialState,
              isInitialized: true,
            },
            false,
            'logout-fallback'
          );
        }
      },

      _setMsalInstance: (instance: any) => set({ _msalInstance: instance }, false, '_setMsalInstance'),

      initializeAuthentication: async msalContext => {
        const { instance: msalInstance, accounts: msalAccounts, inProgress: msalInProgress } = msalContext;
        const isAuthenticated = msalAccounts.length > 0;

        set({ _msalInstance: msalInstance }, false, 'storeMsalInstance');

        if (isAuthenticated && msalAccounts[0]) {
          msalInstance.setActiveAccount(msalAccounts[0]);
        }

        set({ isLoading: msalInProgress !== InteractionStatus.None }, false, 'setLoadingState');

        const { accessToken, embedUrl, isInitialized } = get();

        const eventCallback = msalInstance.addEventCallback((message: EventMessage) => {
          if (message.eventType === EventType.LOGIN_SUCCESS && !accessToken) {
            const payload = message.payload as AuthenticationResult;
            get().handleLoginSuccess(payload);
          }
        });

        const requestWithAccount = {
          ...loginRequest,
          account: msalAccounts[0],
        };

        if (!isAuthenticated && msalInProgress === InteractionStatus.None) {
          msalInstance.loginRedirect(requestWithAccount);
        } else if (isAuthenticated && accessToken && !embedUrl && eventCallback) {
          await get().acquireEmbedUrl();
          msalInstance.removeEventCallback(eventCallback);
        } else if (isAuthenticated && !accessToken && !embedUrl && msalInProgress === InteractionStatus.None) {
          await get().handleSilentTokenAcquisition(msalAccounts[0], msalInstance, requestWithAccount);
        } else if (!isInitialized) {
          set({ isInitialized: true, isLoading: false }, false, 'markInitialized');
        }
      },

      handleLoginSuccess: async (payload: AuthenticationResult) => {
        set({ isLoading: true }, false, 'startLoginProcessing');
        const { _msalInstance } = get();
        const username = payload.account?.username ?? '';
        const displayName = payload.account?.name ?? '';
        const homeAccountId = payload.account?.homeAccountId ?? '';

        if (_msalInstance && payload.account) {
          _msalInstance.setActiveAccount(payload.account);
        }

        set(
          state => ({
            username,
            displayName,
            userId: homeAccountId,
            userRole: username ? get().getRole(username) : Role.BASIC,
            isAuthenticated: payload.accessToken.length > 0 && username.length > 0,
            accessToken: payload.accessToken,
            error: [],
          }),
          false,
          'handleLoginSuccess'
        );

        await tryRefreshUserPermissions(payload.accessToken);

        updateUserInfo({
          name: displayName,
          id: homeAccountId,
          email: username,
        });

        try {
          await getUsersRole();
          set({ isLoading: false }, false, 'loginProcessingComplete');
        } catch (error) {
          console.error('Failed to load user roles:', error);
          set({ isLoading: false }, false, 'loginProcessingError');
        }
      },

      handleSilentTokenAcquisition: async (account: any, msalInstance: any, requestWithAccount: any) => {
        if (msalInstance && account) {
          msalInstance.setActiveAccount(account);
        }

        try {
          const response = await msalInstance.acquireTokenSilent(requestWithAccount);
          set({ accessToken: response.accessToken, error: [] }, false, 'setSilentToken');

          // Code in conditional below does not handled by catch.
          if (account?.username && account?.name) {
            set({ isLoading: true }, false, 'startSilentProcessing');
            const username = account.username;
            const displayName = account.name;
            const homeAccountId = account.homeAccountId || '';

            set(
              state => ({
                username,
                displayName,
                userId: homeAccountId,
                userRole: username ? get().getRole(username) : Role.BASIC,
                isAuthenticated: state.accessToken.length > 0 && username.length > 0,
              }),
              false,
              'updateUserInfo'
            );

            updateUserInfo({
              name: displayName,
              id: homeAccountId,
              email: username,
            });

            try {
              await getUsersRole();
              set({ isLoading: false }, false, 'silentProcessingComplete');
            } catch (error) {
              console.error('Failed to load user roles:', error);
              set({ isLoading: false }, false, 'silentProcessingError');
            }
          }

          await get().acquireEmbedUrl();
        } catch (error) {
          get().handleAuthError(error as AuthError, msalInstance, requestWithAccount);
        } finally {
          set({ isInitialized: true }, false, 'finalizeSilentAcquisition');
        }
      },

      acquireEmbedUrl: async () => {
        const { _msalInstance } = get();
        if (!_msalInstance) return;

        try {
          const response = await _msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: _msalInstance.getActiveAccount(),
          });

          set({ accessToken: response.accessToken }, false, 'updateTokenForEmbed');

          await new Promise<void>((resolve, reject) => {
            getEmbedUrl(
              response.accessToken,
              (url: string) => {
                set({ embedUrl: url, error: [] }, false, 'setEmbedUrl');
                resolve();
              },
              (errors: string[]) => {
                set({ error: errors }, false, 'setEmbedUrlError');
                reject(new Error(errors.join(', ')));
              }
            );
          });
        } catch (error) {
          if (error instanceof Error && 'errorCode' in error) {
            get().handleAuthError(error as AuthError, _msalInstance, {
              ...loginRequest,
              account: _msalInstance.getActiveAccount(),
            });
          }
        } finally {
          set({ isLoading: false, isInitialized: true }, false, 'finalizeEmbedUrl');
        }
      },

      handleAuthError: (error: AuthError, msalInstance: any, loginRequest: any) => {
        set({ isLoading: false }, false, 'stopLoadingOnError');

        if (
          error.errorCode === 'consent_required' ||
          error.errorCode === 'interaction_required' ||
          error.errorCode === 'login_required'
        ) {
          msalInstance.acquireTokenRedirect(loginRequest);
        } else if (error.errorCode === '429') {
          set(
            { error: ['Our Service Token Server (STS) is overloaded, please try again in sometime'] },
            false,
            'setRateLimitError'
          );
        } else {
          set(
            { error: ['There was some problem fetching the access token: ' + error.toString()] },
            false,
            'setGenericError'
          );
        }
      },

      hasRole: (roles: Role[]): boolean => {
        return roles.some(role => {
          if (role === Role.ADMIN) {
            return get().isAdminUser();
          } else if (role === Role.VIEWER) {
            return get().isViewerUser();
          } else if (role === Role.BASIC) {
            return get().isBasicUser();
          } else if (role === Role.CRO) {
            return get().isCROUser();
          } else if (role === Role.CUSTOMER_SUCCESS) {
            return get().isCustomerSuccessUser();
          }
          return false;
        });
      },

      isAdminUser: (): boolean => {
        return adminUsers.some(user => user.toLowerCase() === get().username.toLowerCase());
      },

      isViewerUser: (): boolean => {
        return viewerUsers.some(user => user.toLowerCase() === get().username.toLowerCase());
      },

      isCROUser: (): boolean => {
        return croUsers.some(user => user.toLowerCase() === get().username.toLowerCase());
      },

      isCustomerSuccessUser: (): boolean => {
        return customerSuccessUsers.some(user => user.toLowerCase() === get().username.toLowerCase());
      },

      isBasicUser: (): boolean => {
        return !get().isAdminUser() && !get().isViewerUser() && !get().isCROUser() && !get().isCustomerSuccessUser();
      },

      canDiscount: (): boolean => {
        return get().hasRole([Role.ADMIN, Role.CRO]);
      },

      getRole: (email?: string): Role => {
        const emailToCheck = email || get().username;

        if (adminUsers.some(user => user.toLowerCase() === emailToCheck.toLowerCase())) {
          return Role.ADMIN;
        } else if (viewerUsers.some(user => user.toLowerCase() === emailToCheck.toLowerCase())) {
          return Role.VIEWER;
        } else if (croUsers.some(user => user.toLowerCase() === emailToCheck.toLowerCase())) {
          return Role.CRO;
        } else if (customerSuccessUsers.some(user => user.toLowerCase() === emailToCheck.toLowerCase())) {
          return Role.CUSTOMER_SUCCESS;
        } else {
          return Role.BASIC;
        }
      },

      canApproveRejectApprover: (preset: Preset) => {
        return (
          get().isAdminUser() &&
          (preset?.approvalStatus === ApprovalStatus.PENDING || preset?.approvalStatus === ApprovalStatus.APPROVED_CRO)
        );
      },

      canApproveRejectCRO: (preset: Preset) => {
        return get().isCROUser() && preset?.approvalStatus === ApprovalStatus.PENDING;
      },
    }),
    {
      name: 'auth-store',
    }
  )
);
